/**
 * The surface map: every role-gated web area, the permission it requires, and
 * what a turned-away viewer gets. Layouts and pages pass their key, as a
 * literal, to `requireSurface` (`require-surface.ts`); the nav resolver reads
 * the same entries, so a nav entry and its route can never disagree.
 *
 * The two outcomes differ by area and are kept exactly as they were before the
 * map existed (KTD8). `surface-map-consistency.test.ts` fails when a key has no
 * guard call or a guard names a missing key.
 */

import type { AccessRequirement } from './viewer-access'

/** A redirect target, or a 404 that does not admit the page exists. */
type TurnAway = { redirect: string } | 'notFound'

interface SurfaceEntry {
  /** The routes the entry gates, for readers. */
  area: string
  requires: AccessRequirement
  signedOut: TurnAway
  denied: TurnAway
}

const SCHOOL_DASHBOARD = {
  requires: { school: 'view_school_admin_dashboard' },
  signedOut: { redirect: '/home' },
  denied: { redirect: '/home' }
} as const

const PLATFORM_PAGE = {
  requires: { platform: 'operate_platform' },
  signedOut: { redirect: '/login' },
  denied: { redirect: '/home' }
} as const

export const SURFACES = {
  school: { area: '/school/*', ...SCHOOL_DASHBOARD },
  'school-manage': { area: '/school/manage/*', ...SCHOOL_DASHBOARD },
  'school-comms': { area: '/school/comms/*', ...SCHOOL_DASHBOARD },
  admin: { area: '(admin) group: /admin/*', ...PLATFORM_PAGE },
  tools: { area: '(tools) group', ...PLATFORM_PAGE },
  skylab: { area: '/school/manage/skylab', ...PLATFORM_PAGE },
  'lists-explorer': { area: '/school/manage/explorer/lists', ...PLATFORM_PAGE },
  incidents: {
    area: '/school/comms/incidents/*',
    requires: { platform: 'operate_platform' },
    signedOut: { redirect: '/login' },
    denied: { redirect: '/school/comms' }
  },
  'broadcast-email': {
    area: '/school/comms/broadcasts/create/email/*, /school/comms/broadcasts/templates',
    requires: { platform: 'operate_platform' },
    signedOut: 'notFound',
    denied: 'notFound'
  }
} as const satisfies Record<string, SurfaceEntry>

export type SurfaceKey = keyof typeof SURFACES
